import { motion } from 'framer-motion';
import { MessageSquare, Plus } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface SidebarRailProps {
  threadCount: number;
  onExpand: () => void;
  onCreateThread: () => void;
  onDragStart?: (e: React.MouseEvent) => void; // Optional external drag handler
  className?: string;
}

// Drag threshold in pixels (used when no external handler)
const DRAG_EXPAND_THRESHOLD = 50;

const railVariants = {
  initial: { opacity: 0, x: -20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
};

const springTransition = {
  type: 'spring' as const,
  stiffness: 400,
  damping: 30,
};

const badgeVariants = {
  initial: { scale: 0 },
  animate: { scale: 1 },
};

export const SidebarRail = ({
  threadCount,
  onExpand,
  onCreateThread,
  onDragStart: externalDragStart,
  className,
}: SidebarRailProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const startXRef = useRef(0);

  // Internal drag handler (fallback when no external handler provided)
  const internalDragStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
      startXRef.current = e.clientX;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const deltaX = moveEvent.clientX - startXRef.current;
        if (deltaX > DRAG_EXPAND_THRESHOLD) {
          onExpand();
          handleMouseUp();
        }
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [onExpand],
  );

  // Use external handler if provided, otherwise internal
  const handleDragStart = externalDragStart || internalDragStart;

  return (
    <motion.div
      variants={railVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={springTransition}
      className={cn(
        'flex flex-col items-center py-3 gap-1.5',
        'w-full h-full',
        'bg-slate-50/60 backdrop-blur-sm',
        'border-r border-slate-200/60',
        className,
      )}
    >
      {/* Expand button with thread count */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={onExpand}
            className={cn(
              'relative h-7 w-7 rounded-md',
              'hover:bg-slate-100',
              'transition-colors duration-150',
            )}
          >
            <MessageSquare className="h-4 w-4 text-slate-600" />
            {threadCount > 0 && (
              <motion.span
                variants={badgeVariants}
                initial="initial"
                animate="animate"
                transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 25,
                  delay: 0.1,
                }}
                className={cn(
                  'absolute -top-0.5 -right-0.5',
                  'flex items-center justify-center',
                  'h-3.5 min-w-3.5 px-0.5 rounded-full',
                  'bg-primary text-[9px] font-semibold text-white',
                )}
              >
                {threadCount > 99 ? '99+' : threadCount}
              </motion.span>
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="right">
          <span>
            Expand threads{threadCount > 0 ? ` (${threadCount})` : ''}
          </span>
        </TooltipContent>
      </Tooltip>

      {/* New thread button */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={onCreateThread}
            className={cn(
              'h-7 w-7 rounded-md',
              'hover:bg-slate-100',
              'transition-colors duration-150',
            )}
          >
            <Plus className="h-4 w-4 text-slate-600" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="right">
          <span>New thread</span>
        </TooltipContent>
      </Tooltip>

      {/* Draggable rail indicator - drag right to expand */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className="flex-1 flex items-center justify-center cursor-col-resize"
        onMouseDown={handleDragStart}
      >
        <motion.div
          className="w-0.5 h-10 rounded-full"
          animate={{
            backgroundColor: isDragging
              ? 'rgb(148, 163, 184)'
              : 'rgb(226, 232, 240)',
            height: isDragging ? 48 : 40,
          }}
          whileHover={{
            backgroundColor: 'rgb(148, 163, 184)',
            height: 48,
          }}
          transition={{ duration: 0.15 }}
        />
      </div>
    </motion.div>
  );
};
